import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import * as htmlToImage from 'html-to-image';
import { isLoggedIn } from '../../auth/auth';
import { locationToReturnTo } from '../../auth/returnTo';
import NavBar from '../../components/NavBar.jsx';
import { dataUrlToFile, isNativeApp, shareImageFile } from '../../config/capacitor';
import { usePremiumAccess } from '../../monetization/usePremiumAccess';
import { formatPerUsePrice, formatProductPrice, getChemistryPaymentProducts } from '../../payments/catalog';
import { useMarket } from '../../payments/useMarket';
import { fetchGuestResultUnlock, unlockChemistryPremium } from '../../payments/client';
import {
  buildRestorableChemistryPayload,
  clearChemistryDraft,
  clearChemistryPending,
  readChemistryPending,
  readStoredChemistryResult,
  resetChemistryRecovery,
  resolveChemistryImageSource,
  resolveChemistryMeshOverlay,
  writeStoredChemistryResult,
} from '../../utils/chemistryStorage';
import { getScoreBucket, trackEvent, trackEventOnce } from '../../utils/analytics';
import { useReportAnalytics } from '../../hooks/useReportAnalytics';
import { useTranslation } from 'react-i18next';
import { localizeChemistryResult } from '../../i18n/chemistryResult';
import VisualAttractionReport from './VisualAttractionReport.jsx';
import ChemistryAnalysisSequence from './ChemistryAnalysisSequence.jsx';
import ChemistryVideoGenerator from './ChemistryVideoGenerator.jsx';
import { getChemistryGrade, getDirectionSummary } from './chemistryResultUtils';
import './Chemistry.css';


function ChemistryMeshCard({ imgA, imgB, meshA, meshB, nameA, nameB, t }) {
  const items = [
    { key: 'a', name: nameA, original: imgA, overlay: meshA },
    { key: 'b', name: nameB, original: imgB, overlay: meshB },
  ].filter((item) => item.overlay);

  if (!items.length) return null;

  return (
    <section className="chem-mesh-card" aria-label={t('chemistry_result.analysis_recap.mesh.label')}>
      <div className="chem-mesh-header">
        <h2>{t('chemistry_result.analysis_recap.mesh.title')}</h2>
        <p>{t('chemistry_result.analysis_recap.mesh.description')}</p>
      </div>
      <div className="chem-mesh-list">
        {items.map((item) => (
          <div className="chem-mesh-person" key={item.key}>
            <strong>{item.name}</strong>
            <div className="chem-mesh-grid">
              {item.original ? (
                <figure>
                  <span>{t('chemistry_result.analysis_recap.mesh.original')}</span>
                  <img src={item.original} alt={t('chemistry_result.analysis_recap.mesh.original_alt', { name: item.name })} />
                </figure>
              ) : null}
              <figure>
                <span>{t('chemistry_result.analysis_recap.mesh.overlay')}</span>
                <img src={item.overlay} alt={t('chemistry_result.analysis_recap.mesh.overlay_alt', { name: item.name })} />
              </figure>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

ChemistryMeshCard.propTypes = {
  imgA: PropTypes.string,
  imgB: PropTypes.string,
  meshA: PropTypes.string,
  meshB: PropTypes.string,
  nameA: PropTypes.string.isRequired,
  nameB: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
};

function ChemistryAnalysisRecap({ comfortScore, magnetScore, chemistryScore, chemistryGrade, directionSummary, directionText, t }) {
  const items = [
    { key: 'comfort', label: t('chemistry_result.analysis_recap.comfort'), value: comfortScore, suffix: '', tone: 'mint' },
    { key: 'attraction', label: t('chemistry_result.analysis_recap.attraction'), value: magnetScore, suffix: '', tone: 'pink' },
    { key: 'direction', label: t('chemistry_result.analysis_recap.direction_label'), value: directionText, suffix: '', tone: directionSummary.tone },
    { key: 'final', label: t('chemistry_result.analysis_recap.final'), value: chemistryScore, suffix: t('chemistry_result.analysis_recap.out_of_100'), tone: 'blue' },
  ];

  return (
    <section className="chem-analysis-recap" aria-label={t('chemistry_result.analysis_recap.title')}>
      <div className="chem-analysis-recap-heading">
        <span>{t('chemistry_result.analysis_recap.eyebrow')}</span>
        <h2>{t('chemistry_result.analysis_recap.title')}</h2>
        <p>{t('chemistry_result.analysis_recap.description')}</p>
      </div>
      <div className="chem-analysis-recap-grid">
        {items.map((item) => (
          <div key={item.key} className={`chem-analysis-recap-item tone-${item.tone}`}>
            <span>{item.label}</span>
            <strong>{item.value}<small>{item.suffix}</small></strong>
          </div>
        ))}
      </div>
      <div className="chem-analysis-recap-grade">
        <strong>{chemistryGrade.code}</strong>
        <span>{chemistryGrade.label}</span>
        <p>{chemistryGrade.interpretation}</p>
      </div>
    </section>
  );
}

ChemistryAnalysisRecap.propTypes = {
  comfortScore: PropTypes.number.isRequired,
  magnetScore: PropTypes.number.isRequired,
  chemistryScore: PropTypes.number.isRequired,
  chemistryGrade: PropTypes.shape({
    code: PropTypes.string,
    label: PropTypes.string,
    interpretation: PropTypes.string,
  }).isRequired,
  directionSummary: PropTypes.shape({
    tone: PropTypes.string,
  }).isRequired,
  directionText: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
};

const RECOVERY_MESSAGES = {
  expired: 'The saved chemistry result expired. Please run a new chemistry analysis.',
  corrupt: 'The saved chemistry result could not be restored. Please run a new chemistry analysis.',
  version_mismatch: 'This chemistry result was saved with an older format. Please run a new chemistry analysis.',
};

const SHARE_IMAGE_QUALITY = 0.92;
const SHARE_IMAGE_MAX_PIXEL_RATIO = 2;
const SHARE_PARAM_KEY = 'share';
const ANALYSIS_PHASE = {
  RESTORING: 'restoring',
  REVEALING: 'revealing',
  COMPLETE: 'complete',
  ERROR: 'error',
};

function encodeSharePayload(payload) {
  const json = JSON.stringify(payload);
  return btoa(encodeURIComponent(json)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function decodeSharePayload(value) {
  if (!value) return null;
  try {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    return JSON.parse(decodeURIComponent(atob(padded)));
  } catch {
    return null;
  }
}

function pickShareableChemistryData(source) {
  if (!source || source.status === 'fail') return null;
  return {
    status: source.status,
    title: source.title,
    scores: source.scores,
    vectors: source.vectors,
    user_names: source.user_names,
    directionality: source.directionality,
    attention_flow: source.attention_flow,
    attraction_flow: source.attraction_flow,
    salience: source.salience,
    appeal: source.appeal,
    approachability: source.approachability,
  };
}

function buildShareUrl(source) {
  if (typeof window === 'undefined') return '';
  const payload = pickShareableChemistryData(source);
  const url = new URL('/chemistry-test/result', window.location.origin);
  if (payload) {
    url.searchParams.set(SHARE_PARAM_KEY, encodeSharePayload(payload));
  }
  return url.toString();
}
function getIssueMessage(label, status) {
  const base = RECOVERY_MESSAGES[status];
  if (!base) return label;
  return label ? `${label} ${base}` : base;
}

function buildShareText(userNames, title) {
  const nameA = userNames?.a || 'A';
  const nameB = userNames?.b || 'B';
  const suffix = title ? ` - ${title}` : '';
  return `${nameA} x ${nameB} 케미 결과${suffix}`;
}

function getLiveChemistryImageSource(...sources) {
  return sources.find((source) => typeof source === 'string' && source.trim().length > 0) || null;
}

function isShareCancel(error) {
  const message = String(error?.message || '').toLowerCase();
  return message.includes('cancel') || message.includes('abort');
}

function downloadShareImage(dataUrl, fileName) {
  const link = document.createElement('a');
  link.download = fileName;
  link.href = dataUrl;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

async function waitForImages(node) {
  const images = Array.from(node.querySelectorAll('img'));
  await Promise.all(images.map(async (image) => {
    if (!image.complete) {
      await new Promise((resolve, reject) => {
        image.addEventListener('load', resolve, { once: true });
        image.addEventListener('error', reject, { once: true });
      });
    }
    if (image.complete && !image.naturalWidth) throw new Error('share_image_load_failed');
    if (typeof image.decode === 'function') {
      try {
        await image.decode();
      } catch (error) {
        console.warn('[share-card] image decode failed', error);
      }
    }
  }));
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

async function normalizeShareCardImages(node) {
  await waitForImages(node);
  const replacements = [];
  const restore = () => {
    replacements.forEach(({ image, source }) => { image.src = source; });
  };
  try {
    for (const image of Array.from(node.querySelectorAll('img'))) {
      const source = image.currentSrc || image.src;
      if (!source || source.startsWith('data:')) continue;
      const response = await fetch(source);
      if (!response.ok) throw new Error('share_image_source_unavailable');
      const dataUrl = await blobToDataUrl(await response.blob());
      replacements.push({ image, source: image.src });
      image.src = dataUrl;
    }
    await waitForImages(node);
    return restore;
  } catch (error) {
    restore();
    throw error;
  }
}

function noticeStyle(type) {
  const styles = {
    info: {
      background: 'rgba(59, 130, 246, 0.12)',
      border: '1px solid rgba(96, 165, 250, 0.3)',
      color: '#bfdbfe',
    },
    error: {
      background: 'rgba(239, 68, 68, 0.12)',
      border: '1px solid rgba(248, 113, 113, 0.35)',
      color: '#fecaca',
    },
    success: {
      background: 'rgba(16, 185, 129, 0.12)',
      border: '1px solid rgba(52, 211, 153, 0.28)',
      color: '#a7f3d0',
    },
  };

  return {
    ...styles[type],
    padding: '12px 20px',
    borderRadius: '12px',
    marginBottom: '20px',
    fontSize: '13px',
    fontWeight: '500',
    textAlign: 'center',
  };
}


function consumeAnalysisSequenceNavigationState(state) {
  if (typeof window === 'undefined' || !state?.playAnalysisSequence) return;

  const historyState = window.history.state;
  if (!historyState || typeof historyState !== 'object') return;

  const routerState = historyState.usr;
  if (!routerState || typeof routerState !== 'object') return;

  window.history.replaceState(
    { ...historyState, usr: { ...routerState, playAnalysisSequence: false } },
    document.title,
  );
}

export default function ChemistryResult() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const isEnglish = language.startsWith('en');
  const tr = (ko, en) => isEnglish ? en : ko;
  const packageCopy = (product, field) => t(
    `chemistry_result.report.packages.${product.id}.${field}`,
    { defaultValue: product[field] || '' },
  );
  const { state } = location;
  const premiumAccess = usePremiumAccess('chemistryPremium');
  const { data: marketData } = useMarket();
  const chemistryPaymentProducts = getChemistryPaymentProducts(marketData.market);

  const [data, setData] = useState(null);
  const [restoredPayload, setRestoredPayload] = useState(null);
  const [analysisPhase, setAnalysisPhase] = useState(ANALYSIS_PHASE.RESTORING);
  const [recoveryNotice, setRecoveryNotice] = useState('');
  const [recoveryIssue, setRecoveryIssue] = useState('');
  const [canResumeInput, setCanResumeInput] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [shareNotice, setShareNotice] = useState('');
  const [shareError, setShareError] = useState('');
  const [isPremiumUnlocked, setIsPremiumUnlocked] = useState(false);
  const [isUnlockingPremium, setIsUnlockingPremium] = useState(false);
  const [premiumModalOpen, setPremiumModalOpen] = useState(false);
  const [premiumError, setPremiumError] = useState('');
  const resultViewTrackedRef = React.useRef('');
  const reportRootRef = React.useRef(null);
  const sequenceStartTrackedRef = React.useRef('');
  const sequenceCompleteTrackedRef = React.useRef('');
  const resultRevealedTrackedRef = React.useRef('');
  const analyticsResultKey = String(data?.id || restoredPayload?.sessionId || 'chemistry-result');

  useReportAnalytics({
    rootRef: reportRootRef,
    analysisType: 'chemistry',
    resultKey: analyticsResultKey,
    enabled: Boolean(data && data.status !== 'fail' && analysisPhase === ANALYSIS_PHASE.COMPLETE),
  });

  const hasChemistryPremium = isPremiumUnlocked;
  const isPremiumLoading = Boolean(premiumAccess.loading && premiumAccess.loggedIn);

  const getPremiumResultId = () => String(data?.id || restoredPayload?.data?.id || restoredPayload?.sessionId || '').trim();

  const openPaymentForProduct = (productId) => {
    const currentReturnTo = locationToReturnTo(location, '/chemistry-test/result');
    const params = new URLSearchParams({ returnTo: currentReturnTo, resultId: getPremiumResultId() });
    navigate(`/payments/start/${productId}?${params.toString()}`);
  };

  const handlePremiumReportClick = async () => {
    if (hasChemistryPremium) {
      return;
    }

    trackEvent('result_action_clicked', {
      analysis_type: 'chemistry',
      action_name: 'view_detail',
      source_section: 'detailed_report',
      is_logged_in: isLoggedIn(),
    });

    const resultId = getPremiumResultId();
    if (!resultId) {
      setPremiumError(tr('결과 식별자를 찾지 못했어요.', 'Could not identify this result.'));
      return;
    }
    try {
      setIsUnlockingPremium(true);
      setPremiumError('');
      if (isLoggedIn()) {
        await unlockChemistryPremium(resultId);
        window.dispatchEvent(new Event('premium-entitlements-updated'));
        setIsPremiumUnlocked(true);
        return;
      }
      const guestAccess = await fetchGuestResultUnlock(resultId);
      if (guestAccess?.premium_unlocked) {
        setIsPremiumUnlocked(true);
        return;
      }
      setPremiumModalOpen(true);
      return;
    } catch (unlockError) {
      const message = unlockError?.message || '';
      if (message.includes('402') || message.includes('chemistry_payment_required')) {
        trackEvent('paywall_viewed', {
          product_id: 'chemistry_pass',
          analysis_type: 'chemistry',
          market: marketData.market,
          currency: marketData.currency,
          source_page: 'chemistry_result',
        });
        setPremiumModalOpen(true);
        return;
      }
      setPremiumError(tr('결제 상태를 확인하지 못했어요. 잠시 후 다시 시도해 주세요.', 'Could not verify payment status. Please try again shortly.'));
    } finally {
      setIsUnlockingPremium(false);
    }
  };

  useEffect(() => {
    const storedResultState = readStoredChemistryResult();
    const pendingState = readChemistryPending();
    const sharedData = decodeSharePayload(new URLSearchParams(location.search).get(SHARE_PARAM_KEY));

    if (sharedData?.user_names?.a && sharedData?.user_names?.b) {
      setData(sharedData);
      setRecoveryNotice('Loaded a shared chemistry result.');
      setAnalysisPhase(ANALYSIS_PHASE.COMPLETE);
      return;
    }

    if (state?.data) {
      const nextPayload = buildRestorableChemistryPayload(state, state.data, storedResultState.data);
      if (!nextPayload) {
        setRecoveryIssue('The current chemistry result could not be restored.');
        setAnalysisPhase(ANALYSIS_PHASE.ERROR);
        return;
      }

      const sessionId = writeStoredChemistryResult(nextPayload, {
        sessionId: state?.sessionId || storedResultState.data?.sessionId,
      });

      clearChemistryDraft();
      clearChemistryPending();
      setRestoredPayload({ ...nextPayload, sessionId });
      setData(state.data);
      const shouldPlaySequence = Boolean(state?.playAnalysisSequence) && state.data?.status !== 'fail' && !state.data?.message;
      if (shouldPlaySequence) {
        consumeAnalysisSequenceNavigationState(state);
      }
      setAnalysisPhase(shouldPlaySequence ? ANALYSIS_PHASE.REVEALING : ANALYSIS_PHASE.COMPLETE);
      return;
    }

    if (storedResultState.status === 'available' && storedResultState.data?.data) {
      setRestoredPayload(storedResultState.data);
      setRecoveryNotice('Loaded your most recent chemistry result.');
      setData(storedResultState.data.data);
      setAnalysisPhase(ANALYSIS_PHASE.COMPLETE);
      return;
    }

    if (pendingState.status === 'available' && pendingState.data) {
      setCanResumeInput(true);
      setRecoveryIssue('A previous chemistry session is still available. You can return to the input flow and continue from there.');
      setAnalysisPhase(ANALYSIS_PHASE.COMPLETE);
      return;
    }

    const issues = [];
    if (storedResultState.issue) {
      issues.push(getIssueMessage('Could not restore the saved result.', storedResultState.issue.status));
    }
    if (pendingState.issue) {
      issues.push(getIssueMessage('Could not restore the in-progress chemistry session.', pendingState.issue.status));
    }

    setRecoveryIssue(issues.join(' ') || 'No recent chemistry result was found.');
    setAnalysisPhase(ANALYSIS_PHASE.COMPLETE);
  }, [state, location.search]);

  useEffect(() => {
    if (!data || data.status === 'fail' || analysisPhase !== ANALYSIS_PHASE.COMPLETE) return;

    if (!isLoggedIn()) {
      const resultId = String(data.id || restoredPayload?.sessionId || '').trim();
      if (resultId) {
        fetchGuestResultUnlock(resultId)
          .then((access) => setIsPremiumUnlocked(Boolean(access?.premium_unlocked)))
          .catch(() => {});
      }
    }

    if (data.premium_unlocked) {
      setIsPremiumUnlocked(true);
    }

    const resultKey = String(data.id || restoredPayload?.sessionId || 'chemistry-result');
    if (resultViewTrackedRef.current === resultKey) return;
    resultViewTrackedRef.current = resultKey;

    trackEventOnce('result_viewed', `chemistry:${resultKey}`, {
      feature_type: 'chemistry',
      analysis_type: 'chemistry',
      result_type: data.type_code || data.grade_code,
      score_bucket: getScoreBucket(data.scores?.chemistry),
      is_paid_result: Boolean(data.premium_unlocked || isPremiumUnlocked),
      entry_source: new URLSearchParams(location.search).has(SHARE_PARAM_KEY)
        ? 'shared_result'
        : (state?.data ? 'new_analysis' : 'restored_result'),
      is_logged_in: isLoggedIn(),
    });
  }, [analysisPhase, data, isPremiumUnlocked, location.search, restoredPayload?.sessionId, state?.data]);

  useEffect(() => {
    if (analysisPhase !== ANALYSIS_PHASE.REVEALING || !data || data.status === 'fail') return;
    const resultKey = String(data.id || restoredPayload?.sessionId || 'chemistry-result');
    if (sequenceStartTrackedRef.current === resultKey) return;
    sequenceStartTrackedRef.current = resultKey;
    trackEvent('chemistry_analysis_sequence_start', {
      test_type: 'chemistry',
      source: 'new_analysis',
    });
  }, [analysisPhase, data, restoredPayload?.sessionId]);

  const handleStartFresh = () => {
    trackEvent('result_action_clicked', {
      analysis_type: 'chemistry',
      action_name: 'retry',
      source_section: 'result_error',
    });
    resetChemistryRecovery();
    navigate('/chemistry-test');
  };

  const handleResumeInput = () => {
    trackEvent('result_action_clicked', {
      analysis_type: 'chemistry',
      action_name: 'retry',
      source_section: 'result_recovery',
    });
    navigate('/chemistry-test');
  };

  if (analysisPhase === ANALYSIS_PHASE.RESTORING) {
    return (
      <div
        className="layout-wrapper"
        style={{ background: 'linear-gradient(160deg, #080812 0%, #0f0d1e 50%, #080812 100%)', minHeight: '100dvh' }}
        aria-busy="true"
      />
    );
  }

  if (recoveryIssue || !data) {
    return (
      <div className="layout-wrapper light-theme">
        <NavBar theme="light" />
        <div className="layout-content chemistry-container">
          <div className="error-card">
            <h2>Could not load the result</h2>
            <p>{recoveryIssue || 'No restorable chemistry result is available.'}</p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              {canResumeInput ? (
                <button onClick={handleResumeInput} className="chem-submit-btn">
                  Return to chemistry input
                </button>
              ) : null}
              <button onClick={handleStartFresh} className="chem-submit-btn">
                Start a new analysis
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const localizedData = localizeChemistryResult(data, t);
  const {
    title,
    scores,
    vectors,
    user_names,
    message,
    directionality,
    attention_flow,
    attraction_flow,
    salience,
    appeal,
    approachability,
  } = localizedData;
  const imgA = getLiveChemistryImageSource(
    state?.previewA,
    state?.imageA,
    data?.input_images?.a,
    data?.images?.a,
    data?.images?.user_a,
  ) || resolveChemistryImageSource(state, data, restoredPayload, 'A');
  const imgB = getLiveChemistryImageSource(
    state?.previewB,
    state?.imageB,
    data?.input_images?.b,
    data?.images?.b,
    data?.images?.user_b,
  ) || resolveChemistryImageSource(state, data, restoredPayload, 'B');
  const meshA = resolveChemistryMeshOverlay(state, data, restoredPayload, 'A');
  const meshB = resolveChemistryMeshOverlay(state, data, restoredPayload, 'B');
  const chemistryScore = Math.round(scores?.chemistry || 0);
  const magnetScore = Math.round(scores?.magnet || 0);
  const comfortScore = Math.round(scores?.mirror || 0);
  const chemistryGrade = getChemistryGrade(chemistryScore, magnetScore, comfortScore, language);
  const safeNameA = user_names?.a || 'Person A';
  const safeNameB = user_names?.b || 'Person B';
  const shareUrl = buildShareUrl(localizedData);
  const directionSummary = getDirectionSummary(attraction_flow || attention_flow || directionality, safeNameA, safeNameB);
  const directionText = t(`chemistry_result.analysis_recap.direction.${directionSummary.titleKey}`, {
    nameA: safeNameA,
    nameB: safeNameB,
  });
  const handleSequenceComplete = () => {
    const resultKey = String(data.id || restoredPayload?.sessionId || 'chemistry-result');
    if (sequenceCompleteTrackedRef.current !== resultKey) {
      sequenceCompleteTrackedRef.current = resultKey;
      trackEvent('chemistry_analysis_sequence_complete', {
        test_type: 'chemistry',
        chemistry_score: chemistryScore,
        grade_code: chemistryGrade.code,
      });
    }
    if (resultRevealedTrackedRef.current !== resultKey) {
      resultRevealedTrackedRef.current = resultKey;
    }
    setAnalysisPhase(ANALYSIS_PHASE.COMPLETE);
  };

  const handleSequenceSkip = (scene) => {
    trackEvent('chemistry_analysis_sequence_skip', {
      test_type: 'chemistry',
      scene: scene || 'unknown',
    });
  };

  if (analysisPhase === ANALYSIS_PHASE.REVEALING) {
    return (
      <ChemistryAnalysisSequence
        imageA={imgA}
        imageB={imgB}
        meshImageA={meshA}
        meshImageB={meshB}
        nameA={safeNameA}
        nameB={safeNameB}
        result={localizedData}
        language={language}
        onComplete={handleSequenceComplete}
        onSkip={handleSequenceSkip}
      />
    );
  }

  if (message || data.status === 'fail') {
    return (
      <div className="layout-wrapper light-theme">
        <NavBar theme="light" />
        <div className="layout-content chemistry-container">
          <div className="error-card">
            <h2>Analysis failed</h2>
            <p>{message || 'Chemistry analysis could not be completed.'}</p>
            <button onClick={handleStartFresh} className="chem-submit-btn">
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const createShareImage = async () => {
    const cardNode = document.querySelector('#chemistry-share-capture .chem-report-hero');
    if (!cardNode) throw new Error('share_target_missing');
    let restoreImageSources = () => {};
    try {
      if (document.fonts?.ready) await document.fonts.ready;
      restoreImageSources = await normalizeShareCardImages(cardNode);
      const pixelRatio = Math.min(window.devicePixelRatio || 1, SHARE_IMAGE_MAX_PIXEL_RATIO);
      const dataUrl = await htmlToImage.toJpeg(cardNode, {
        quality: SHARE_IMAGE_QUALITY,
        cacheBust: false,
        pixelRatio,
        backgroundColor: '#11111c',
        width: cardNode.scrollWidth,
        height: cardNode.scrollHeight,
        style: { transform: 'none', overflow: 'visible' },
      });
      trackEvent('chemistry_share_image_generate_complete', { test_type: 'chemistry' });
      return dataUrl;
    } catch (error) {
      trackEvent('chemistry_share_image_generate_fail', {
        analysis_type: 'chemistry',
        failure_reason: 'share_image_generation_failed',
      });
      throw error;
    } finally {
      restoreImageSources();
    }
  };

  const handleSaveCard = async () => {
    if (isSaving || isSharing) return;
    trackEvent('result_action_clicked', {
      analysis_type: 'chemistry',
      action_name: 'download',
      source_section: 'detailed_report',
    });
    try {
      setIsSaving(true);
      setShareNotice('');
      setShareError('');
      const dataUrl = await createShareImage();
      const fileName = `chemistry-result-${user_names?.a || 'a'}-${user_names?.b || 'b'}.jpeg`;
      downloadShareImage(dataUrl, fileName);
      setShareNotice(tr('결과 페이지 일부를 이미지로 저장했어요.', 'Part of the result page was saved as an image.'));
    } catch (error) {
      console.error('Failed to save chemistry result image:', error);
      setShareError(tr('결과 이미지를 저장하지 못했어요. 다시 시도해 주세요.', 'The result image could not be saved. Please try again.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleShare = async () => {
    if (isSharing || isSaving) return;
    trackEvent('result_action_clicked', {
      analysis_type: 'chemistry',
      action_name: 'share',
      source_section: 'detailed_report',
    });
    try {
      setIsSharing(true);
      setShareNotice('');
      setShareError('');
      const dataUrl = await createShareImage();
      const fileName = `chemistry-result-${user_names?.a || 'a'}-${user_names?.b || 'b'}.jpeg`;
      const shareFile = dataUrlToFile(dataUrl, fileName.replace(/\.jpeg$/i, ''));
      const shareText = `${buildShareText(user_names, title)}
${shareUrl}`;
      const didShare = await shareImageFile({
        title: 'Start-Us Chemistry Result',
        text: shareText,
        file: shareFile,
      });
      if (didShare || isNativeApp()) {
        setShareNotice(tr('결과 이미지와 공유 링크를 함께 보냈어요.', 'The result image and share link are ready to send.'));
        trackEvent('share_completed', {
          analysis_type: 'chemistry',
          share_method: 'native_share',
        });
      } else {
        downloadShareImage(dataUrl, fileName);
        await handleCopyLink({ trackAction: false });
        setShareNotice(tr('공유 기능을 사용할 수 없어 이미지를 저장하고 링크를 복사했어요.', 'Sharing is unavailable, so the image was saved and the link was copied.'));
      }
    } catch (error) {
      if (isShareCancel(error)) {
        setShareNotice(tr('공유를 취소했어요.', 'Sharing was cancelled.'));
      } else {
        console.error('Failed to share chemistry result:', error);
        setShareError(tr('결과를 공유하지 못했어요. 다시 시도해 주세요.', 'The result could not be shared. Please try again.'));
      }
    } finally {
      setIsSharing(false);
    }
  };

  const handleCopyLink = async (options = {}) => {
    if (options?.trackAction !== false) {
      trackEvent('result_action_clicked', {
        analysis_type: 'chemistry',
        action_name: 'share',
        source_section: 'detailed_report',
      });
    }
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        textArea.setAttribute('readonly', '');
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        const copied = document.execCommand('copy');
        textArea.remove();
        if (!copied) throw new Error('clipboard_copy_failed');
      }
      setShareError('');
      setShareNotice(tr('공유용 결과 링크를 복사했어요.', 'The shareable result link was copied.'));
      trackEvent('share_completed', {
        analysis_type: 'chemistry',
        share_method: 'copy_link',
      });
    } catch (error) {
      console.error('Failed to copy chemistry result link:', error);
      setShareError(tr('링크를 복사하지 못했어요. 주소창의 링크를 직접 복사해 주세요.', 'The link could not be copied. Copy it directly from the address bar.'));
    }
  };

  return (
    <div
      className="layout-wrapper"
      style={{ background: 'linear-gradient(160deg, #080812 0%, #0f0d1e 50%, #080812 100%)', minHeight: '100vh' }}
    >
      <NavBar theme="dark" />
      <div className="funnel-screen">
        <div ref={reportRootRef} className="funnel-shell funnel-stack chemistry-result-screen" style={{ maxWidth: '760px' }}>
          {recoveryNotice ? <div style={noticeStyle('info')}>{recoveryNotice}</div> : null}
          {shareNotice || shareError ? (
            <div style={noticeStyle(shareError ? 'error' : 'info')}>{shareError || shareNotice}</div>
          ) : null}
          {premiumError ? <div style={noticeStyle('error')}>{premiumError}</div> : null}

          {hasChemistryPremium ? (
            <div style={noticeStyle('success')}>
              {tr('이 결과의 프리미엄 해석이 열렸습니다.', 'Premium insights are unlocked for this result.')}
            </div>
          ) : null}
          <div data-analytics-section="summary">
            <ChemistryAnalysisRecap
              comfortScore={comfortScore}
              magnetScore={magnetScore}
              chemistryScore={chemistryScore}
              chemistryGrade={chemistryGrade}
              directionSummary={directionSummary}
              directionText={directionText}
              t={t}
            />
          </div>

          <div data-analytics-section="face_mesh">
            <ChemistryMeshCard
              imgA={imgA}
              imgB={imgB}
              meshA={meshA}
              meshB={meshB}
              nameA={safeNameA}
              nameB={safeNameB}
              t={t}
            />
          </div>

          <div data-analytics-section="recommendation">
            <ChemistryVideoGenerator
              result={localizedData}
              imageA={imgA}
              imageB={imgB}
              meshImageA={meshA}
              meshImageB={meshB}
              nameA={safeNameA}
              nameB={safeNameB}
              language={language}
              shareText={buildShareText(user_names, title)}
            />
          </div>

          <div id="chemistry-share-capture" data-analytics-section="detailed_report" style={{ marginBottom: '32px' }}>
            <VisualAttractionReport
              nameA={safeNameA}
              nameB={safeNameB}
              imageA={imgA}
              imageB={imgB}
              rankTitle={title}
              chemistryScore={chemistryScore}
              gradeCode={chemistryGrade.code}
              gradeLabel={chemistryGrade.label}
              rarityLabel={chemistryGrade.rarity}
              scoreInterpretation={chemistryGrade.interpretation}
              attractionIntensity={magnetScore}
              perceivedComfort={comfortScore}
              vectorA={vectors?.user_a}
              vectorB={vectors?.user_b}
              attentionFlow={attention_flow}
              attractionFlow={attraction_flow}
              directionality={directionality}
              salienceA={salience?.user_a}
              salienceB={salience?.user_b}
              appealA={appeal?.user_a}
              appealB={appeal?.user_b}
              approachabilityA={approachability?.user_a}
              approachabilityB={approachability?.user_b}
              onPremiumClick={handlePremiumReportClick}
              isPremiumUnlocked={hasChemistryPremium}
              isPremiumLoading={isPremiumLoading || isUnlockingPremium}
              onSave={handleSaveCard}
              onShare={handleShare}
              onCopyLink={handleCopyLink}
              isSaving={isSaving}
              isSharing={isSharing}
            />
          </div>

          {premiumModalOpen ? (
            <div className="chem-package-modal-backdrop" role="presentation" onClick={() => setPremiumModalOpen(false)}>
              <div className="chem-package-modal" role="dialog" aria-modal="true" aria-labelledby="chem-package-title" onClick={(event) => event.stopPropagation()}>
                <div className="chem-package-modal-header">
                  <span className="chem-premium-badge">Premium</span>
                  <button type="button" className="chem-package-close" onClick={() => setPremiumModalOpen(false)} aria-label={tr('닫기', 'Close')}>
                    x
                  </button>
                  <h2 id="chem-package-title">{tr('케미 분석 + 프리미엄 해석 이용권', 'Chemistry Analysis + Premium Insights Pass')}</h2>
                  <p>{tr('횟수를 선택하세요. 추가 분석과 해당 결과의 프리미엄 해석에 1회씩 사용됩니다.', 'Choose a pass. Each use covers one additional analysis and the premium insights for that result.')}</p>
                </div>

                <div className="chem-package-stack">
                  {chemistryPaymentProducts.map((product) => (
                    <div
                      key={product.id}
                      className={`chem-package-card ${product.recommended ? 'is-recommended' : ''}`}
                    >
                      <div className="chem-package-card-top">
                        <div>
                          <strong>{packageCopy(product, 'title')}</strong>
                          <span>{packageCopy(product, 'tagline')}</span>
                        </div>
                        {packageCopy(product, 'badge') ? <em>{packageCopy(product, 'badge')}</em> : null}
                      </div>
                      <div className="chem-package-price-row">
                        <span>{formatProductPrice(product, language)}</span>
                        <small>{formatPerUsePrice(product, language)}</small>
                      </div>
                      <p>{packageCopy(product, 'description')}</p>
                      <button
                        type="button"
                        className={`chem-package-pay-btn ${product.recommended ? 'is-recommended' : ''}`}
                        onClick={() => openPaymentForProduct(product.id)}
                      >
                        {tr('결제하기', 'Continue to payment')}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
